import { CommonModule } from '@angular/common';
import { Component, EventEmitter, OnDestroy, OnInit, Output, inject, signal } from '@angular/core';
import { Router, RouterModule, RouteReuseStrategy } from '@angular/router';
import { SearchService } from '../../shared/services/search.service';
import { AuthService } from '../../auth/services/auth.service';
import { User } from '../../auth/models/user.model';
import { RuntimeService } from '../../shared/services/runtime.service';
import { TabWorkspaceService } from '../tab-workspace.service';
import { TabRouteReuseStrategy } from '../tab-route-reuse.strategy';
import { NetworkStatusBadgeComponent } from '../../shared/network-status-badge/network-status-badge.component';

interface AnimatedPlane {
  id: string;
  img: string;
  size: number;
  angle: number;
  startX: number; // percent
  startY: number; // percent
  endX: number;   // percent
  endY: number;   // percent
  currentX: number; // percent
  currentY: number; // percent
  duration: number; // seconds
  startTime: number; // ms
  trailLength: number; // px
  trailOpacity: number;
  flip: boolean; // true if the plane is flying left to right
}

@Component({
  selector: 'app-header',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    NetworkStatusBadgeComponent
  ],
  templateUrl: './header.component.html',
  styleUrl: './header.component.scss'
})
export class HeaderComponent implements OnInit, OnDestroy {
  activeTab = signal('flight-list');
  utcTime: string = '';
  utcDate: string = '';
  private timer: any;
  @Output() searchEvent = new EventEmitter<string>();
  weatherClass = '';
  user: User | null = null;
  readonly IsDesktopApplication = inject(RuntimeService).IsDesktopApplication;

  hasSun = false;
  hasClouds = false;
  hasRain = false;
  hasThunder = false;
  hasSnow = false;

  clouds: any[] = [];
  rainDrops: any[] = [];  
  lightningBolts: any[] = [];
  snowflakes: any[] = [];

  flashActive = false;
  flashX = 50;
  flashY = 30;

  planes: AnimatedPlane[] = [];
  animationFrameId: number | null = null;

  constructor(private searchService: SearchService,
    private authService: AuthService,
    private tabWorkspaceService: TabWorkspaceService,
    private router: Router,
    private routeReuseStrategy: RouteReuseStrategy
  ) {}

  ngOnInit() {
    this.setWeather(''); // Example weather, can be set dynamically
    this.updateUtcTime();
    this.timer = setInterval(() => this.updateUtcTime(), 1000);
    this.user = this.authService.getStoredUser();
  }

  setWeather(weather: string) {
    this.weatherClass = `weather-${weather}`;

    // Reset flags
    this.hasSun = weather.includes('sunny');
    this.hasClouds = weather.includes('clouds');
    this.hasRain = weather.includes('rain');
    this.hasThunder = weather.includes('thunder');
    this.hasSnow = weather.includes('snow');

    // Conditionally generate visuals
    if (this.hasClouds) this.generateClouds();
    if (this.hasRain) this.generateRain();
    if (this.hasThunder) this.generateLightning();
    if (this.hasThunder) this.startLightningFlashes();
    if (this.hasSnow) this.generateSnow();
    
  }

  generateClouds() {
      this.clouds = Array.from({ length: 6 + Math.floor(Math.random() * 4) }).map(() => ({
      width: 60 + Math.random() * 80, // 60px to 140px
      height: 30 + Math.random() * 30, // 30px to 60px
      top: 5 + Math.random() * 40, // 5px to 45px
      left: -100 - Math.random() * 100, // start off-screen left
      duration: 210 + Math.random() * 60, // 210s to 270s
      timing: this.randomCubicBezier(), // random cubic-bezier for variety
      bobAmp1: Math.random() * 5 + 5, // 5px to 10px
      bobAmp2: Math.random() * 5 + 5, // 5px to 10px
      bobAmp3: Math.random() * 5 + 5, // 5px to 10px
      bobPhase: Math.random() * 2 * Math.PI, // random phase for bobbing
    }));
  }

  randomCubicBezier() {
    // Generate cubic-bezier values for smoother, more even animation
    // Keep start/end slow, but reduce middle speed by bringing p2 and p3 closer to 0.5
    const p1 = (0.15 + Math.random() * 0.1).toFixed(2); // 0.15 - 0.25
    const p2 = (0.40 + Math.random() * 0.15).toFixed(2); // 0.40 - 0.55 (closer to linear)
    const p3 = (0.40 + Math.random() * 0.15).toFixed(2); // 0.40 - 0.55 (closer to linear)
    const p4 = (0.75 + Math.random() * 0.1).toFixed(2); // 0.75 - 0.85
    return `cubic-bezier(${p1},${p2},${p3},${p4})`;
  }

  generateRain() {
    this.rainDrops = Array.from({ length: Math.floor(Math.random() * 40) + 10 }).map(() => ({
      left: Math.random() * 100, // percent
      delay: Math.random(),      // seconds
      duration: 0.6 + Math.random() * 0.6, // seconds
      angle: 0 // degrees
    }));
  }

  generateLightning() {
    this.lightningBolts = Array.from({ length: 3 }).map(() => {
      const delay = Math.random() * 8;

      // Schedule flash for the bolt
      setTimeout(() => {
        this.triggerCloudFlashIrregular(Math.floor(Math.random() * 2) + 2);
      }, delay * 1000); // convert to ms

      return {
        left: 1 + Math.random() * 99, // percent, between 1% and 99%
        delay: Math.random() * 8,      // 0s to 8s
        duration: 2 + Math.random() * 2, // 2s to 4s
        angle: Math.random() * 30 - 15 // degrees, random angle between -15 and +15 degrees
      };
    });
  }

  triggerCloudFlashIrregular(flashes: number = 2) {
    this.flashX = 20 + Math.random() * 60; // random between 20% and 80%
    this.flashY = 10 + Math.random() * 60; // random between 10% and 70%

    let count = 0;

    const flash = () => {
      this.flashActive = true;
      setTimeout(() => {
        this.flashActive = false;
      }, 40 + Math.random() * 40); // 40–80ms on

      count++;
      if (count < flashes) {
        setTimeout(flash, 60 + Math.random() * 60); // 60–120ms gap
      }
    };

    flash();
  }

  startLightningFlashes() {
    if (!this.hasThunder) return;

    setInterval(() => {
      // Inside lightning logic
      this.triggerCloudFlashIrregular(Math.floor(Math.random() * 2) + 2); // 2 or 3 flashes
    }, 3000 + Math.random() * 5000); // every 3–8s
  }

  generateSnow() {
    this.snowflakes = Array.from({ length: 100 }).map(() => ({
      left: Math.random() * 100, // percent
      size: 2 + Math.random() * 3, // px
      duration: 2.5 + Math.random() * 2, // seconds
      delay: Math.random() * 2, // seconds
      x: -10 + Math.random() * 20 // px, for horizontal drift
    }));
  }

  onSearchChange(searchValue: Event): void {
    const input = searchValue.target as HTMLInputElement | null;
    if (input) {
      console.log('Search value changed:', input.value);
      this.searchService.updateSearchValue(input.value);
    }
  }

  onMasterManagementClick(event: MouseEvent): void {
    // Prevent any default navigation; this click should only toggle the dropdown.
    event.preventDefault();
    event.stopPropagation();
  }

  get isMasterManagementActive(): boolean {
    return this.router.url.startsWith('/master-management');
  }

  get isLoadPlanningActive(): boolean {
    // Highlight when on /load-planning or any child route except /load-planning/chat
    return this.router.url.startsWith('/load-planning') && !this.router.url.startsWith('/load-planning/chat');
  }

  get hasChatAccess(): boolean {
    return this.authService.hasChatAccess();
  }

  get isChatActive(): boolean {
    return this.router.url.startsWith('/load-planning/chat');
  }

  ngOnDestroy() {
    if (this.timer) clearInterval(this.timer);
    if (this.animationFrameId) cancelAnimationFrame(this.animationFrameId);
  }

  updateUtcTime() {
    const now = new Date();
    // Format time as HH:mm:ss (UTC)
    this.utcTime = now.toISOString().substring(11, 19);

    // Format date as e.g. Sun, Mar 21,2025
    const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const day = days[now.getUTCDay()];
    const month = months[now.getUTCMonth()];
    const date = now.getUTCDate();
    const year = now.getUTCFullYear();
    this.utcDate = `${day}, ${month} ${date},${year}`;
  }

spawnPlanes() {
    const count = Math.floor(Math.random() * 2) + 2; // 2-3 planes
    for (let i = 0; i < count; i++) {
      const direction = Math.random() > 0.5 ? 'bl-tr' : 'br-tl';
      // Start/end offscreen for full diagonal
      const startX = direction === 'bl-tr' ? -10 : 110;
      const endX = direction === 'bl-tr' ? 110 : -10;
      const startY = 110;
      const endY = -10;
      const dx = endX - startX;
      const dy = endY - startY;
      const angle = 0;
      const flip = direction === 'br-tl'; // true if flying left to right
      const size = 40 + Math.random() * 20;
      const duration = 7 + Math.random() * 4; // 7-11s
      const now = performance.now();
      this.planes.push({
        id: 'plane-' + now + '-' + i,
        img: 'assets/airplane.svg', // your flat SVG
        size,
        angle,
        startX, startY, endX, endY,
        currentX: startX,
        currentY: startY,
        duration,
        startTime: now,
        trailLength: 0,
        trailOpacity: 1,
        flip
      });
    }
  }

  animatePlanes() {
    const now = performance.now();
    this.planes.forEach((plane, idx) => {
      const elapsed = (now - plane.startTime) / 1000;
      const t = Math.min(elapsed / plane.duration, 1);
      // Linear interpolation
      plane.currentX = plane.startX + (plane.endX - plane.startX) * t;
      plane.currentY = plane.startY + (plane.endY - plane.startY) * t;
      plane.trailLength = plane.size * 4 * t; // trail grows with progress
      plane.trailOpacity = 1 - t; // fades out as plane leaves
    });
    // Remove planes that have finished
    this.planes = this.planes.filter(plane => (now - plane.startTime) / 1000 < plane.duration);
    this.animationFrameId = requestAnimationFrame(() => this.animatePlanes());
  }

  logout() {
    this.authService.logout();
    const strategy = this.routeReuseStrategy as TabRouteReuseStrategy;
    strategy.clearAll?.();
    this.tabWorkspaceService.clearTabs();
  }

  goHome() {
    // if user is in planing stage
    // then don't dont reload and also dont navigate to home screen in electron env
    if(this.IsDesktopApplication) {
      const currentRoute = this.router.url;
      if (currentRoute.startsWith('/load-planning/plan/')) {
        return;
      }
    } else {
      this.router.navigateByUrl('/', { skipLocationChange: false }).then(() => {
        window.location.reload();
      });
    }
  }

  goToLoadPlanning() {
    if (this.IsDesktopApplication) {
      const currentRoute = this.router.url;
      if (currentRoute.startsWith('/load-planning/plan/')) {
        return;
      }
    }
    this.router.navigate(['/load-planning']);
  }

  openChatInNewWindow() {
    const urlTree = this.router.createUrlTree(['/load-planning/chat']);
    const url = this.router.serializeUrl(urlTree);
    window.open(url, '_blank', 'noopener,noreferrer');
  }

}
